import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity,
  ArchiveRestore,
  ChevronRight,
  Cloud,
  Download,
  FileDown,
  Filter,
  FolderKanban,
  KeyRound,
  LayoutDashboard,
  ListTodo,
  LogOut,
  Moon,
  MoreHorizontal,
  Plus,
  Search,
  ReceiptText,
  SlidersHorizontal,
  Sparkles,
  Target,
  Sun,
  Upload,
  Users,
  X,
} from 'lucide-react'
import type { User } from 'firebase/auth'
import { AuthScreen } from './components/AuthScreen'
import { ClientPanel } from './components/ClientPanel'
import { ImportModal } from './components/ImportModal'
import { OperationsHub } from './components/OperationsHub'
import { Planner } from './components/Planner'
import { SecurityModal } from './components/SecurityModal'
import {
  ActiveClientsPanel,
  DailySummaryPanel,
  FollowUpTaskSync,
} from './components/WorkspacePanels'
import {
  deleteCloudClient,
  observeUser,
  replaceCloudClients,
  saveCloudClient,
  saveCloudClients,
  signOutUser,
  watchClients,
} from './lib/cloud'
import { createDemoClients } from './lib/demo'
import { catalogSearchText } from './lib/catalog'
import { exportBackup, exportCsv, readBackup } from './lib/exporter'
import { allowedEmail, clearFirestoreDeviceCache, firebaseConfigured } from './lib/firebase'
import { calculatePriority, hasSpoken, isDue, sortByPriority } from './lib/priority'
import {
  clearState,
  createEmptyState,
  loadState,
  replaceState,
  saveState,
} from './lib/storage'
import type { AppState, Client, Invoice, OsintImportResult, WorkProject } from './types'
import { CLIENT_STATUS_OPTIONS } from './types'
import {
  deleteInvoice,
  deleteProject,
  saveInvoice,
  saveProject,
  watchInvoices,
  watchProjects,
} from './workspace/operationsCloud'
import {
  workspaceSearchForView,
  workspaceViewFromSearch,
  type WorkspaceView,
} from './workspace/navigation'

type QuickFilter = 'all' | 'uncontacted' | 'spoken' | 'no_answer' | 'due' | 'important' | 'important_without_next'
type Theme = 'light' | 'dark'
type CatalogSort = 'priority' | 'rank' | 'revenue' | 'company'

interface CatalogFilters {
  segment: string
  revenueCategory: string
  legalType: string
  companyStatus: string
  governmentContracts: string
  sort: CatalogSort
}

const emptyCatalogFilters: CatalogFilters = {
  segment: '',
  revenueCategory: '',
  legalType: '',
  companyStatus: '',
  governmentContracts: '',
  sort: 'priority',
}

const quickFilters: Array<{ value: QuickFilter; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'uncontacted', label: 'Ещё не общались' },
  { value: 'spoken', label: 'Уже взаимодействовали' },
  { value: 'no_answer', label: 'Не дозвонился' },
  { value: 'due', label: 'Просрочен контакт' },
  { value: 'important', label: 'В работе' },
]

const workspaceViews: Array<{
  value: WorkspaceView
  label: string
  eyebrow: string
  icon: typeof LayoutDashboard
}> = [
  { value: 'today', label: 'Рабочий день', eyebrow: 'Личный рабочий пульт', icon: LayoutDashboard },
  { value: 'tasks', label: 'Задачи', eyebrow: 'План и напоминания', icon: ListTodo },
  { value: 'clients', label: 'Клиенты', eyebrow: 'Клиентская база', icon: Users },
  { value: 'projects', label: 'Проекты', eyebrow: 'Текущая работа', icon: FolderKanban },
  { value: 'invoices', label: 'Счета', eyebrow: 'Контроль оплаты', icon: ReceiptText },
  { value: 'summary', label: 'Итоги дня', eyebrow: 'Журнал действий', icon: Activity },
]

function createBlankClient(): Client {
  const now = new Date().toISOString()
  return {
    id: crypto.randomUUID(),
    company: '',
    contactPerson: '',
    phone: '',
    email: '',
    city: '',
    segment: '',
    potential: null,
    paymentTermsDays: 30,
    status: 'new',
    important: false,
    attempts: 0,
    lastContactAt: null,
    nextContactAt: null,
    result: '',
    notes: '',
    interactions: [],
    createdAt: now,
    updatedAt: now,
  }
}

function formatMoney(value: number | null): string {
  if (value === null) return '—'
  return new Intl.NumberFormat('ru-RU', {
    notation: value >= 1_000_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
    style: 'currency',
    currency: 'RUB',
  }).format(value)
}

function formatContactDate(value: string | null): string {
  if (!value) return 'Не назначен'
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function statusLabel(client: Client): string {
  return CLIENT_STATUS_OPTIONS.find((status) => status.value === client.status)?.label ?? client.status
}

function governmentContractsLabel(client: Client): string {
  if (client.osint?.governmentContracts === 'yes') {
    return client.osint.minimumContracts
      ? `Да · от ${client.osint.minimumContracts}`
      : 'Да'
  }
  if (client.osint?.governmentContracts === 'not_found') return 'Не найдено'
  return 'Нет данных'
}

function uniqueCatalogValues(
  clients: Client[],
  field: (client: Client) => string | undefined,
): string[] {
  return [...new Set(clients.map(field).filter((value): value is string => Boolean(value)))]
    .sort((left, right) => left.localeCompare(right, 'ru'))
}

function sortCatalogClients(clients: Client[], sort: CatalogSort): Client[] {
  if (sort === 'priority') return sortByPriority(clients)
  if (sort === 'company') {
    return [...clients].sort((left, right) => left.company.localeCompare(right.company, 'ru'))
  }
  if (sort === 'revenue') {
    return [...clients].sort((left, right) => {
      const delta = (right.osint?.revenue ?? -1) - (left.osint?.revenue ?? -1)
      return delta || left.company.localeCompare(right.company, 'ru')
    })
  }
  return [...clients].sort((left, right) => {
    const leftRank = left.osint?.rank ?? Number.POSITIVE_INFINITY
    const rightRank = right.osint?.rank ?? Number.POSITIVE_INFINITY
    return leftRank - rightRank || left.company.localeCompare(right.company, 'ru')
  })
}

function App() {
  const restoreInputRef = useRef<HTMLInputElement>(null)
  const localStateRef = useRef<AppState>(createEmptyState())
  const pendingWritesRef = useRef(0)
  const migrationHandledRef = useRef(false)
  const [state, setState] = useState<AppState>(createEmptyState())
  const [user, setUser] = useState<User | null>(null)
  const [authReady, setAuthReady] = useState(false)
  const [ready, setReady] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<QuickFilter>('all')
  const [catalogFilters, setCatalogFilters] =
    useState<CatalogFilters>(emptyCatalogFilters)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [securityOpen, setSecurityOpen] = useState(false)
  const [toast, setToast] = useState('')
  const [projects, setProjects] = useState<WorkProject[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [clock, setClock] = useState(() => new Date())
  const [activeView, setActiveView] = useState<WorkspaceView>(() =>
    workspaceViewFromSearch(window.location.search),
  )
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('worksmarter-theme')
    if (saved === 'light' || saved === 'dark') return saved
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem('worksmarter-theme', theme)
  }, [theme])

  useEffect(() => {
    const handleHistory = () => setActiveView(workspaceViewFromSearch(window.location.search))
    window.addEventListener('popstate', handleHistory)
    return () => window.removeEventListener('popstate', handleHistory)
  }, [])

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 30_000)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    if (!firebaseConfigured) {
      setAuthReady(true)
      return
    }
    return observeUser((currentUser) => {
      if (
        currentUser?.email?.toLocaleLowerCase('en-US') !== allowedEmail &&
        currentUser !== null
      ) {
        void signOutUser()
        setUser(null)
      } else {
        setUser(currentUser)
      }
      setAuthReady(true)
    })
  }, [])

  useEffect(() => {
    if (!user) {
      setReady(false)
      setState(createEmptyState())
      return
    }

    let disposed = false
    let unsubscribe = () => {}
    migrationHandledRef.current = false
    setReady(false)

    void loadState()
      .then((saved) => {
        if (disposed) return
        localStateRef.current = saved
        setState(saved)
        setReady(true)

        unsubscribe = watchClients(
          user.uid,
          (remoteClients, fromCache) => {
            if (disposed) return

            if (
              !fromCache &&
              !migrationHandledRef.current &&
              remoteClients.length === 0 &&
              localStateRef.current.clients.length > 0
            ) {
              migrationHandledRef.current = true
              const localClients = localStateRef.current.clients
              const shouldUpload = window.confirm(
                `В облаке пока пусто. Перенести туда локальную базу (${localClients.length} клиентов)?`,
              )
              if (shouldUpload) {
                pendingWritesRef.current += 1
                setSaving(true)
                void saveCloudClients(user.uid, localClients)
                  .then(() => setToast('Локальная база перенесена в облако'))
                  .catch(() => setToast('Не удалось перенести локальную базу. Резервная копия сохранена на устройстве.'))
                  .finally(() => {
                    pendingWritesRef.current -= 1
                    setSaving(pendingWritesRef.current > 0)
                  })
                return
              }
            }

            if (!fromCache) migrationHandledRef.current = true
            if (remoteClients.length > 0 || !fromCache) {
              const nextState: AppState = {
                schemaVersion: 1,
                clients: remoteClients,
                lastSavedAt: new Date().toISOString(),
              }
              localStateRef.current = nextState
              setState(nextState)
            }
          },
          () => setToast('Не удалось получить данные из облака. Работаем с локальной копией.'),
        )
      })
      .catch(() => {
        setToast('Не удалось открыть локальное хранилище.')
        setReady(true)
      })

    return () => {
      disposed = true
      unsubscribe()
    }
  }, [user])

  useEffect(() => {
    if (!user) {
      setProjects([])
      setInvoices([])
      return
    }
    const stopProjects = watchProjects(
      user.uid,
      setProjects,
      () => setToast('Не удалось синхронизировать проекты.'),
    )
    const stopInvoices = watchInvoices(
      user.uid,
      setInvoices,
      () => setToast('Не удалось синхронизировать счета.'),
    )
    return () => {
      stopProjects()
      stopInvoices()
    }
  }, [user])

  useEffect(() => {
    if (!ready || !user) return
    const timer = window.setTimeout(() => {
      const nextState = { ...state, lastSavedAt: new Date().toISOString() }
      localStateRef.current = nextState
      void saveState(nextState)
        .catch(() => setToast('Не удалось сохранить изменения. Выгрузите резервную копию.'))
    }, 350)
    return () => window.clearTimeout(timer)
  }, [ready, state, user])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(''), 4200)
    return () => window.clearTimeout(timer)
  }, [toast])

  const clients = state.clients
  const selectedClient = clients.find((client) => client.id === selectedId) ?? null

  const filteredClients = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('ru')
    const filtered = clients.filter((client) => {
      const matchesSearch =
        !query ||
        catalogSearchText(client).includes(query)

      if (!matchesSearch) return false
      if (filter === 'uncontacted') return client.status === 'new'
      if (filter === 'spoken') return hasSpoken(client.status)
      if (filter === 'no_answer') return client.status === 'no_answer'
      if (filter === 'due') return isDue(client)
      if (filter === 'important') return client.important
      if (filter === 'important_without_next') {
        return client.important && !client.nextContactAt && client.status !== 'not_interested'
      }
      return true
    }).filter((client) => {
      const osint = client.osint
      if (catalogFilters.segment && (osint?.segment || client.segment) !== catalogFilters.segment) {
        return false
      }
      if (
        catalogFilters.revenueCategory &&
        osint?.revenueCategory !== catalogFilters.revenueCategory
      ) {
        return false
      }
      if (catalogFilters.legalType && osint?.legalType !== catalogFilters.legalType) {
        return false
      }
      if (
        catalogFilters.companyStatus &&
        osint?.companyStatus !== catalogFilters.companyStatus
      ) {
        return false
      }
      if (
        catalogFilters.governmentContracts &&
        osint?.governmentContracts !== catalogFilters.governmentContracts
      ) {
        return false
      }
      return true
    })
    return sortCatalogClients(filtered, catalogFilters.sort)
  }, [catalogFilters, clients, filter, search])

  const catalogOptions = useMemo(
    () => ({
      segments: uniqueCatalogValues(clients, (client) => client.osint?.segment || client.segment),
      revenueCategories: uniqueCatalogValues(
        clients,
        (client) => client.osint?.revenueCategory,
      ),
      legalTypes: uniqueCatalogValues(clients, (client) => client.osint?.legalType),
      companyStatuses: uniqueCatalogValues(
        clients,
        (client) => client.osint?.companyStatus,
      ),
    }),
    [clients],
  )

  const activeCatalogFilterCount = useMemo(
    () =>
      [
        catalogFilters.segment,
        catalogFilters.revenueCategory,
        catalogFilters.legalType,
        catalogFilters.companyStatus,
        catalogFilters.governmentContracts,
      ].filter(Boolean).length,
    [catalogFilters],
  )

  const stats = useMemo(() => {
    const spoken = clients.filter((client) => hasSpoken(client.status)).length
    const untouched = clients.filter((client) => client.status === 'new').length
    const due = clients.filter((client) => isDue(client)).length
    const important = clients.filter((client) => client.important).length
    const progress = clients.length ? Math.round((spoken / clients.length) * 100) : 0
    return { spoken, untouched, due, important, progress }
  }, [clients])

  const attentionSignals = useMemo(
    () => ({
      due: clients.filter((client) => isDue(client)).length,
      withoutNextStep: clients.filter(
        (client) =>
          client.important &&
          !client.nextContactAt &&
          client.status !== 'not_interested',
      ).length,
      noAnswer: clients.filter((client) => client.status === 'no_answer').length,
      unprocessed: clients.filter((client) => client.status === 'new').length,
    }),
    [clients],
  )

  function setClients(nextClients: Client[]) {
    setState((current) => ({ ...current, clients: nextClients }))
  }

  function navigate(view: WorkspaceView, replace = false) {
    const nextUrl = `${window.location.pathname}${workspaceSearchForView(window.location.search, view)}${window.location.hash}`
    window.history[replace ? 'replaceState' : 'pushState']({ view }, '', nextUrl)
    setActiveView(view)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function openClients(nextFilter: QuickFilter = 'all') {
    setSearch('')
    setCatalogFilters((current) => ({
      ...emptyCatalogFilters,
      sort: current.sort,
    }))
    setFilter(nextFilter)
    navigate('clients')
  }

  function openClient(client: Client) {
    setFilter('all')
    setSearch('')
    setSelectedId(client.id)
    navigate('clients')
  }

  async function persistCloud(action: () => Promise<void>, successMessage?: string) {
    pendingWritesRef.current += 1
    setSaving(true)
    try {
      await action()
      if (successMessage) setToast(successMessage)
    } catch {
      setToast('Изменение осталось на устройстве, но пока не синхронизировано.')
    } finally {
      pendingWritesRef.current -= 1
      setSaving(pendingWritesRef.current > 0)
    }
  }

  function saveClient(updated: Client) {
    setClients(clients.map((client) => (client.id === updated.id ? updated : client)))
    void persistCloud(() => saveCloudClient(user!.uid, updated), 'Карточка сохранена')
  }

  function storeProject(project: WorkProject) {
    setProjects((current) => [project, ...current.filter((item) => item.id !== project.id)])
    void persistCloud(() => saveProject(user!.uid, project), 'Проект сохранён')
  }

  function removeProject(project: WorkProject) {
    if (!window.confirm(`Удалить проект № ${project.number}? Счета останутся в истории.`)) return false
    setProjects((current) => current.filter((item) => item.id !== project.id))
    void persistCloud(() => deleteProject(user!.uid, project.id), 'Проект удалён')
    return true
  }

  function storeInvoice(invoice: Invoice) {
    setInvoices((current) => [invoice, ...current.filter((item) => item.id !== invoice.id)])
    void persistCloud(() => saveInvoice(user!.uid, invoice), 'Счёт сохранён')
  }

  function createClientForInvoice(input: { company: string; taxId: string; paymentTermsDays: number }) {
    const client: Client = {
      ...createBlankClient(),
      company: input.company,
      taxId: input.taxId,
      paymentTermsDays: input.paymentTermsDays,
    }
    setClients([client, ...clients])
    void persistCloud(() => saveCloudClient(user!.uid, client), 'Клиент создан и выбран в счёте')
    return client
  }

  function removeInvoice(invoice: Invoice) {
    if (!window.confirm(`Удалить счёт № ${invoice.number}?`)) return false
    setInvoices((current) => current.filter((item) => item.id !== invoice.id))
    void persistCloud(() => deleteInvoice(user!.uid, invoice.id), 'Счёт удалён')
    return true
  }

  function addClient() {
    const client = createBlankClient()
    setClients([client, ...clients])
    setSelectedId(client.id)
    void persistCloud(() => saveCloudClient(user!.uid, client))
  }

  function deleteClient(client: Client) {
    if (!window.confirm(`Удалить клиента «${client.company || 'Без названия'}»?`)) return
    setClients(clients.filter((item) => item.id !== client.id))
    setSelectedId(null)
    void persistCloud(() => deleteCloudClient(user!.uid, client.id), 'Клиент удалён')
  }

  function importClients(imported: Client[], skipped: number) {
    setClients([...clients, ...imported])
    setImportOpen(false)
    void persistCloud(
      () => saveCloudClients(user!.uid, imported),
      `Импортировано: ${imported.length}${skipped ? ` · пропущено дублей/пустых: ${skipped}` : ''}`,
    )
  }

  function importCatalog(result: OsintImportResult) {
    setClients(result.clients)
    setImportOpen(false)
    void persistCloud(
      () => replaceCloudClients(user!.uid, clients, result.clients),
      `Каталог обновлён: ${result.updated} сопоставлено · ${result.created} добавлено${
        result.skipped ? ` · ${result.skipped} пропущено` : ''
      }`,
    )
  }

  async function restoreBackup(file?: File) {
    if (!file) return
    try {
      const restored = await readBackup(file)
      if (!window.confirm(`Заменить текущие данные резервной копией (${restored.clients.length} клиентов)?`)) {
        return
      }
      await replaceState(restored)
      setState(restored)
      setSelectedId(null)
      await persistCloud(
        () => replaceCloudClients(user!.uid, clients, restored.clients),
        'Резервная копия восстановлена',
      )
    } catch (reason) {
      setToast(reason instanceof Error ? reason.message : 'Не удалось восстановить копию.')
    } finally {
      if (restoreInputRef.current) restoreInputRef.current.value = ''
    }
  }

  function loadDemo() {
    if (clients.length && !window.confirm('Добавить демонстрационных клиентов к текущей базе?')) return
    const demoClients = createDemoClients()
    setClients([...clients, ...demoClients])
    void persistCloud(
      () => saveCloudClients(user!.uid, demoClients),
      'Демонстрационные клиенты добавлены',
    )
  }

  function clearAll() {
    if (!window.confirm('Удалить всех клиентов из облака и с этого устройства? Перед этим выгрузите резервную копию.')) return
    const previousClients = clients
    setState(createEmptyState())
    setSelectedId(null)
    void persistCloud(
      () => replaceCloudClients(user!.uid, previousClients, []),
      'База очищена',
    )
  }

  async function signOutAndClearDevice() {
    setReady(false)
    try {
      await clearState()
    } catch {}
    try {
      await clearFirestoreDeviceCache()
    } catch {}
    try {
      await signOutUser()
    } finally {
      window.location.reload()
    }
  }

  if (!authReady) {
    return (
      <main className="loading-screen">
        <div className="logo-mark">WS</div>
        <p>Проверяю доступ…</p>
      </main>
    )
  }

  if (!user) return <AuthScreen />

  if (!ready) {
    return (
      <main className="loading-screen">
        <div className="logo-mark">WS</div>
        <p>Синхронизирую рабочее пространство…</p>
      </main>
    )
  }

  const currentView = workspaceViews.find((item) => item.value === activeView) ?? workspaceViews[0]

  return (
    <div className={`app-shell ${selectedClient ? 'with-panel' : ''}`}>
      <aside className="workspace-sidebar" aria-label="Навигация рабочего пространства">
        <button className="workspace-sidebar-brand" type="button" onClick={() => navigate('today')}>
          <span className="logo-mark">WS</span>
          <span>
            <strong>WorkSmarter</strong>
            <small>Рабочий пульт</small>
          </span>
        </button>
        <nav className="workspace-sidebar-nav">
          {workspaceViews.map((item) => {
            const Icon = item.icon
            return (
              <button
                type="button"
                key={item.value}
                className={activeView === item.value ? 'active' : ''}
                aria-current={activeView === item.value ? 'page' : undefined}
                onClick={() => navigate(item.value)}
              >
                <Icon size={19} /><span>{item.label}</span>
              </button>
            )
          })}
        </nav>
        <div className="workspace-sidebar-note">
          <Cloud size={17} />
          <span>Данные синхронизируются с облаком</span>
        </div>
      </aside>

      <div className="workspace-stage">
      <header className="topbar">
        <div className="workspace-header-title">
          <span>{currentView.eyebrow}</span>
          <strong>{currentView.label}</strong>
        </div>
        <div className="brand topbar-brand">
          <div className="logo-mark">WS</div>
          <div>
            <strong>WorkSmarter</strong>
            <span>Рабочий пульт менеджера</span>
          </div>
        </div>
        <div className="topbar-actions">
          <span className={`save-state ${saving ? 'saving' : ''}`}>
            <Cloud size={15} />
            {saving ? 'Синхронизирую…' : 'Сохранено в облаке'}
          </span>
          <span className="user-chip" title={user.email ?? ''}>{user.email}</span>
          <button
            className="icon-button"
            type="button"
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            aria-label="Переключить тему"
          >
            {theme === 'light' ? <Moon size={19} /> : <Sun size={19} />}
          </button>
          <button
            className="icon-button"
            type="button"
            onClick={() => void signOutAndClearDevice()}
            aria-label="Выйти"
            title="Выйти и удалить локальную копию"
          >
            <LogOut size={19} />
          </button>
          <details className="actions-menu">
            <summary className="icon-button" aria-label="Дополнительные действия">
              <MoreHorizontal size={20} />
            </summary>
            <div className="menu-popover">
              <button type="button" onClick={() => setSecurityOpen(true)}>
                <KeyRound size={17} /> Безопасность входа
              </button>
              <button type="button" onClick={() => exportBackup(state)}>
                <Download size={17} /> Резервная копия
              </button>
              <button type="button" onClick={() => exportCsv(clients)} disabled={!clients.length}>
                <FileDown size={17} /> Выгрузить CSV
              </button>
              <button type="button" onClick={() => restoreInputRef.current?.click()}>
                <ArchiveRestore size={17} /> Восстановить копию
              </button>
              <button className="danger" type="button" onClick={clearAll} disabled={!clients.length}>
                <X size={17} /> Очистить базу
              </button>
            </div>
          </details>
          <button className="button secondary desktop-action" type="button" onClick={() => setImportOpen(true)}>
            <Upload size={18} />
            Импорт
          </button>
          <button className="button primary" type="button" onClick={addClient}>
            <Plus size={18} />
            Добавить клиента
          </button>
        </div>
      </header>

      <main className="main-content" id="workspace-top">
        <section className="workspace-overview-bar workspace-view" hidden={activeView !== 'today'} aria-label="Обзор рабочего дня">
          <div className="workspace-overview-copy">
            <span className="eyebrow">Личный рабочий пульт</span>
            <h1>Рабочий день</h1>
            <div className="workspace-overview-progress">
              <strong>{stats.progress}%</strong>
              <span>{stats.spoken} из {clients.length} клиентов обработано</span>
            </div>
            <div className="workspace-clock" aria-label="Текущие дата и время">
              <time dateTime={clock.toISOString()}>
                {new Intl.DateTimeFormat('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'Europe/Moscow',
                }).format(clock)}
              </time>
              <span>
                {new Intl.DateTimeFormat('ru-RU', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  timeZone: 'Europe/Moscow',
                }).format(clock)}
              </span>
            </div>
          </div>
          <div className="workspace-overview-note">
            <span>Сегодня в фокусе</span>
            <strong>{attentionSignals.due + attentionSignals.withoutNextStep}</strong>
            <small>контактов требуют решения</small>
          </div>
        </section>

        <section className="stats-grid workspace-view" hidden={activeView !== 'clients'} aria-label="Статистика клиентов">
          <button type="button" onClick={() => openClients('all')} className={filter === 'all' ? 'active' : ''}>
            <span className="stat-icon blue"><Users size={20} /></span>
            <div><strong>{clients.length}</strong><span>Всего клиентов</span></div>
          </button>
          <button type="button" onClick={() => openClients('uncontacted')} className={filter === 'uncontacted' ? 'active' : ''}>
            <span className="stat-icon violet"><PhoneIcon /></span>
            <div><strong>{stats.untouched}</strong><span>Ещё не общались</span></div>
          </button>
          <button type="button" onClick={() => openClients('spoken')} className={filter === 'spoken' ? 'active' : ''}>
            <span className="stat-icon orange"><CalendarIcon /></span>
            <div><strong>{stats.spoken}</strong><span>Уже взаимодействовали</span></div>
          </button>
          <button type="button" onClick={() => openClients('important')} className={filter === 'important' ? 'active' : ''}>
            <span className="stat-icon green"><Sparkles size={20} /></span>
            <div><strong>{stats.important}</strong><span>В работе</span></div>
          </button>
        </section>

        <section
          className={`workspace-dashboard workspace-view ${activeView === 'tasks' ? 'tasks-view' : ''}`}
          hidden={activeView !== 'today' && activeView !== 'tasks'}
          aria-label="Основная рабочая зона"
        >
          <div className="workspace-dashboard-primary">
            <Planner userId={user.uid} mode={activeView === 'tasks' ? 'full' : 'compact'} />
          </div>
          {activeView === 'today' ? (
            <aside className="workspace-dashboard-secondary" aria-label="Клиенты в работе">
              <ActiveClientsPanel
                clients={clients}
                onOpenClient={openClient}
                onShowAll={() => openClients('important')}
              />
            </aside>
          ) : null}
        </section>

        <div className="workspace-view" hidden={activeView !== 'today'}>
          <OperationsHub
            clients={clients}
            projects={projects}
            invoices={invoices}
            onSaveProject={storeProject}
            onDeleteProject={removeProject}
            onSaveInvoice={storeInvoice}
            onDeleteInvoice={removeInvoice}
            onCreateClient={createClientForInvoice}
          />
        </div>

        <section className="workspace-radar workspace-view" hidden={activeView !== 'today'} aria-label="Требует внимания">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Рабочий радар</span>
              <h2>Требует внимания</h2>
            </div>
            <span>Нажмите на сигнал, чтобы открыть соответствующих клиентов.</span>
          </div>
          <div className="workspace-radar-grid">
            <button
              type="button"
              className={filter === 'due' ? 'active' : ''}
              onClick={() => openClients('due')}
            >
              <span className="radar-icon orange"><CalendarIcon /></span>
              <div><strong>{attentionSignals.due}</strong><span>Контакт наступил</span></div>
              <small>Назначенная дата уже сегодня или прошла</small>
            </button>
            <button
              type="button"
              className={filter === 'important_without_next' ? 'active' : ''}
              onClick={() => openClients('important_without_next')}
            >
              <span className="radar-icon violet"><Target size={19} /></span>
              <div><strong>{attentionSignals.withoutNextStep}</strong><span>В работе без шага</span></div>
              <small>Активные клиенты без следующего контакта</small>
            </button>
            <button
              type="button"
              className={filter === 'no_answer' ? 'active' : ''}
              onClick={() => openClients('no_answer')}
            >
              <span className="radar-icon blue"><PhoneIcon /></span>
              <div><strong>{attentionSignals.noAnswer}</strong><span>Не дозвонились</span></div>
              <small>Стоит решить, когда повторить попытку</small>
            </button>
            <button
              type="button"
              className={filter === 'uncontacted' ? 'active' : ''}
              onClick={() => openClients('uncontacted')}
            >
              <span className="radar-icon green"><Users size={19} /></span>
              <div><strong>{attentionSignals.unprocessed}</strong><span>Новые не разобраны</span></div>
              <small>Клиенты, с которыми ещё не взаимодействовали</small>
            </button>
          </div>
        </section>

        <section className="workspace-view workspace-standalone" hidden={activeView !== 'projects'}>
          <OperationsHub
            mode="projects"
            clients={clients}
            projects={projects}
            invoices={invoices}
            onSaveProject={storeProject}
            onDeleteProject={removeProject}
            onSaveInvoice={storeInvoice}
            onDeleteInvoice={removeInvoice}
            onCreateClient={createClientForInvoice}
          />
        </section>

        <section className="workspace-view workspace-standalone" hidden={activeView !== 'invoices'}>
          <OperationsHub
            mode="invoices"
            clients={clients}
            projects={projects}
            invoices={invoices}
            onSaveProject={storeProject}
            onDeleteProject={removeProject}
            onSaveInvoice={storeInvoice}
            onDeleteInvoice={removeInvoice}
            onCreateClient={createClientForInvoice}
          />
        </section>

        <section className="workspace-view workspace-summary-view" hidden={activeView !== 'summary'}>
          <DailySummaryPanel userId={user.uid} clients={clients} onOpenClient={openClient} />
        </section>

        <section className="clients-section workspace-view" hidden={activeView !== 'clients'}>
          <div className="clients-toolbar">
            <div>
              <span className="eyebrow">Клиентская база</span>
              <h2>{filteredClients.length.toLocaleString('ru-RU')} клиентов</h2>
            </div>
            <div className="toolbar-controls">
              <label className="search-box">
                <Search size={18} />
                <input
                  type="search"
                  placeholder="Компания, контакт, телефон…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                {search ? (
                  <button type="button" onClick={() => setSearch('')} aria-label="Очистить поиск"><X size={16} /></button>
                ) : null}
              </label>
              <button className="button secondary mobile-import" type="button" onClick={() => setImportOpen(true)}>
                <Upload size={18} /> Импорт
              </button>
            </div>
          </div>

          <div className="filter-row">
            <Filter size={17} />
            {quickFilters.map((item) => (
              <button
                type="button"
                key={item.value}
                className={filter === item.value ? 'active' : ''}
                onClick={() => setFilter(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="catalog-filters">
            <div className="catalog-filter-heading">
              <span>
                <SlidersHorizontal size={16} />
                Каталог
                {activeCatalogFilterCount ? <b>{activeCatalogFilterCount}</b> : null}
              </span>
              <button
                className="text-button"
                type="button"
                disabled={!activeCatalogFilterCount}
                onClick={() =>
                  setCatalogFilters((current) => ({
                    ...emptyCatalogFilters,
                    sort: current.sort,
                  }))
                }
              >
                Сбросить
              </button>
            </div>
            <div className="catalog-filter-grid">
              <label>
                <span>Сегмент</span>
                <select
                  value={catalogFilters.segment}
                  onChange={(event) =>
                    setCatalogFilters((current) => ({
                      ...current,
                      segment: event.target.value,
                    }))
                  }
                >
                  <option value="">Все сегменты</option>
                  {catalogOptions.segments.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Категория выручки</span>
                <select
                  value={catalogFilters.revenueCategory}
                  onChange={(event) =>
                    setCatalogFilters((current) => ({
                      ...current,
                      revenueCategory: event.target.value,
                    }))
                  }
                >
                  <option value="">Любая выручка</option>
                  {catalogOptions.revenueCategories.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Тип</span>
                <select
                  value={catalogFilters.legalType}
                  onChange={(event) =>
                    setCatalogFilters((current) => ({
                      ...current,
                      legalType: event.target.value,
                    }))
                  }
                >
                  <option value="">ЮЛ и ИП</option>
                  {catalogOptions.legalTypes.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Статус компании</span>
                <select
                  value={catalogFilters.companyStatus}
                  onChange={(event) =>
                    setCatalogFilters((current) => ({
                      ...current,
                      companyStatus: event.target.value,
                    }))
                  }
                >
                  <option value="">Любой статус</option>
                  {catalogOptions.companyStatuses.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Госконтракты</span>
                <select
                  value={catalogFilters.governmentContracts}
                  onChange={(event) =>
                    setCatalogFilters((current) => ({
                      ...current,
                      governmentContracts: event.target.value,
                    }))
                  }
                >
                  <option value="">Любой результат</option>
                  <option value="yes">Подтверждены</option>
                  <option value="not_found">Не найдены</option>
                  <option value="unknown">Нет данных</option>
                </select>
              </label>
              <label>
                <span>Сортировка</span>
                <select
                  value={catalogFilters.sort}
                  onChange={(event) =>
                    setCatalogFilters((current) => ({
                      ...current,
                      sort: event.target.value as CatalogSort,
                    }))
                  }
                >
                  <option value="priority">Рабочий приоритет</option>
                  <option value="rank">Рейтинг отчёта</option>
                  <option value="revenue">Выручка</option>
                  <option value="company">Название</option>
                </select>
              </label>
            </div>
          </div>

          {clients.length ? (
            filteredClients.length ? (
              <div className="table-scroll client-table-wrap">
                <table className="client-table">
                  <thead>
                    <tr>
                      <th>Приоритет</th>
                      <th>Клиент</th>
                      <th>Категория</th>
                      <th>Контакт</th>
                      <th>Выручка / потенциал</th>
                      <th>Госконтракты</th>
                      <th>Статус</th>
                      <th>Следующий контакт</th>
                      <th><span className="sr-only">Открыть</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => {
                      const priority = calculatePriority(client)
                      return (
                        <tr
                          key={client.id}
                          className={selectedId === client.id ? 'selected' : ''}
                          onClick={() => setSelectedId(client.id)}
                        >
                          <td data-label="Приоритет"><span className={`priority-badge wide ${priority.level}`}>{priority.score} · {priority.label}</span></td>
                          <td data-label="Клиент">
                            <strong>{client.company || 'Без названия'}</strong>
                            <span>
                              {client.osint?.rank ? `#${client.osint.rank} · ` : ''}
                              {client.taxId ? `ИНН ${client.taxId}` : client.city || '—'}
                            </span>
                          </td>
                          <td data-label="Категория">
                            <strong>{client.osint?.segment || client.segment || '—'}</strong>
                            <span>
                              {[client.osint?.legalType, client.osint?.revenueCategory]
                                .filter(Boolean)
                                .join(' · ') || 'Без категории'}
                            </span>
                          </td>
                          <td data-label="Контакт">
                            <strong>{client.contactPerson || '—'}</strong>
                            <span>{client.phone || client.email || 'Нет контактов'}</span>
                          </td>
                          <td data-label="Выручка">
                            <strong>{formatMoney(client.osint?.revenue ?? client.potential)}</strong>
                            <span>
                              {client.osint?.revenue
                                ? `выручка${client.osint.revenueYear ? ` · ${client.osint.revenueYear}` : ''}`
                                : client.potential !== null
                                  ? 'потенциал'
                                  : 'нет данных'}
                            </span>
                          </td>
                          <td data-label="Госконтракты">
                            <strong className={client.osint?.governmentContracts === 'yes' ? 'positive-text' : ''}>
                              {governmentContractsLabel(client)}
                            </strong>
                            <span>{client.osint?.latestContract?.laws || '—'}</span>
                          </td>
                          <td data-label="Статус"><span className={`status-pill ${client.status}`}>{statusLabel(client)}</span></td>
                          <td data-label="Следующий контакт" className={isDue(client) ? 'due-date' : ''}>{formatContactDate(client.nextContactAt)}</td>
                          <td className="client-row-arrow"><ChevronRight size={18} /></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state small">
                <Search size={28} />
                <h3>Ничего не найдено</h3>
                <p>Измените поиск или фильтр.</p>
                <button className="text-button" type="button" onClick={() => { setSearch(''); setFilter('all') }}>
                  Сбросить фильтры
                </button>
              </div>
            )
          ) : (
            <div className="empty-state">
              <span className="empty-icon"><Users size={30} /></span>
              <h3>Начните с клиентской базы</h3>
              <p>Загрузите Excel или CSV — WorkSmarter распознает колонки и создаст очередь для звонков.</p>
              <div>
                <button className="button primary" type="button" onClick={() => setImportOpen(true)}>
                  <Upload size={18} /> Загрузить базу
                </button>
                <button className="button secondary" type="button" onClick={loadDemo}>
                  Показать пример
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
      <nav className="workspace-mobile-nav" aria-label="Основная навигация">
        {workspaceViews.slice(0, 3).map((item) => {
          const Icon = item.icon
          return (
            <button
              type="button"
              key={item.value}
              className={activeView === item.value ? 'active' : ''}
              aria-current={activeView === item.value ? 'page' : undefined}
              onClick={() => navigate(item.value)}
            >
              <Icon size={20} />
              <span>{item.value === 'today' ? 'День' : item.label}</span>
            </button>
          )
        })}
        <details className="workspace-mobile-more">
          <summary className={['projects', 'invoices', 'summary'].includes(activeView) ? 'active' : ''}>
            <MoreHorizontal size={20} />
            <span>Ещё</span>
          </summary>
          <div>
            {workspaceViews.slice(3).map((item) => {
              const Icon = item.icon
              return (
                <button
                  type="button"
                  key={item.value}
                  className={activeView === item.value ? 'active' : ''}
                  onClick={(event) => {
                    navigate(item.value)
                    event.currentTarget.closest('details')?.removeAttribute('open')
                  }}
                >
                  <Icon size={18} /> {item.label}
                </button>
              )
            })}
          </div>
        </details>
      </nav>
      </div>

      <FollowUpTaskSync userId={user.uid} clients={clients} />

      {selectedClient ? (
        <ClientPanel
          client={selectedClient}
          onClose={() => setSelectedId(null)}
          onDelete={deleteClient}
          onSave={saveClient}
          invoices={invoices.filter((invoice) => invoice.clientId === selectedClient.id)}
          onSaveInvoice={storeInvoice}
        />
      ) : null}

      {importOpen ? (
        <ImportModal
          existingClients={clients}
          onClose={() => setImportOpen(false)}
          onImport={importClients}
          onCatalogImport={importCatalog}
        />
      ) : null}

      {securityOpen ? (
        <SecurityModal user={user} onClose={() => setSecurityOpen(false)} />
      ) : null}

      <input
        ref={restoreInputRef}
        className="sr-only"
        type="file"
        accept="application/json,.json"
        onChange={(event) => void restoreBackup(event.target.files?.[0])}
      />

      {toast ? <div className="toast" role="status">{toast}</div> : null}
    </div>
  )
}

function PhoneIcon() {
  return <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.69 2.8a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.85.56 2.81.69A2 2 0 0 1 22 16.92Z" /></svg>
}

function CalendarIcon() {
  return <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" /></svg>
}

export default App
